import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Spin } from 'antd';


/**
 * MeshViewer Component
 * 
 * 3D mesh viewer component using Three.js for rendering STL and PLY files.
 * Provides interactive 3D visualization with orbit controls and automatic
 * camera positioning based on mesh geometry.
 * 
 * Features:
 * - STL and PLY file support
 * - Interactive orbit controls
 * - Automatic camera positioning
 * - Loading state management
 * - Responsive rendering
 * - Auto-rotation capability
 * 
 * @param {Object} props - Component props
 * @param {string} props.url - URL of the mesh file to load
 * @param {number} props.width - Width of the viewer
 * @param {number} props.height - Height of the viewer
 * @returns {JSX.Element} The mesh viewer component
 */
const MeshViewer = ({ url, width, height }) => {
    const mountRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(width, height);
        renderer.setClearColor(0xffffff, 1);

        const mountCurrent = mountRef.current;
        mountCurrent.appendChild(renderer.domElement);

        const light = new THREE.HemisphereLight(0xffffff, 0x080820, 1.5);
        scene.add(light);

        camera.position.z = 5;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableRotate = true;
        controls.enableDamping = true;
        controls.enablePan = true;
        controls.rotateSpeed = 0.05;
        controls.dampingFactor = 0.1;
        controls.enableZoom = true;
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.75;

        const fileType = url.split('.').pop().toLowerCase();
        const loader = fileType === 'stl' ? new STLLoader() : new PLYLoader();

        loader.load(url, (geometry) => {
            setIsLoading(false); // Set loading to false when the geometry is loaded

            let material = new THREE.MeshPhongMaterial({
                color: 0xffffff,
                specular: 100,
                shininess: 100
            });

            if (fileType === 'ply') {
                geometry.computeVertexNormals();
            }

            const mesh = new THREE.Mesh(geometry, material);
            scene.add(mesh);
            mesh.position.set(0, 0, 0);

            const geo = mesh.geometry;
            let middle = new THREE.Vector3();
            geo.computeBoundingBox();
            geo.boundingBox.getCenter(middle);

            geo.applyMatrix4(
                new THREE.Matrix4().makeTranslation(-middle.x, -middle.y, -middle.z)
            );

            let bb = geo.boundingBox;
            let mD = Math.max(bb.max.x, bb.max.y, bb.max.z);
            camera.position.z = mD * 3;

            animate();
        });

        function animate() {
            requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        }

        return () => {
            if (mountCurrent) {
                mountCurrent.removeChild(renderer.domElement);
            }
        };
    }, [url, width, height]); // Dependencies for useEffect

    return (
        <div ref={mountRef}>
            {isLoading && <Spin />}
        </div>
    );
};

export default MeshViewer;